import {
  useCallback,
  useEffect,
  useState,
  useRef,
  FunctionComponent,
} from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../firebaseConfig";
import { Button, createTypedForm, Text, Icon } from "@src/straps/base";
import AsyncButton from "@src/straps/derived/AsyncButton/AsyncButton";
import { emailRegex } from "@src/global_functions/validation";
import classNames from "classnames";
import { useMutation } from "react-query";
import { AuthWrapper, Heading, PasswordVisibilityToggle } from "./components";
import { FirebaseError } from "firebase/app";
import { throwFirebaseError } from "./error";
import { ErrorBoundary } from "@src/global_components";

import PageTransition from "@src/straps/utils/PageTransition/PageTransition";
import backpackSdk from "@src/global_functions/backpackSdk/sdk";

type CredentialsForm = {
  email: string;
  password: string;
};
const { Input, Form } = createTypedForm<CredentialsForm>();

type RequestPasswordResetFormType = {
  passwordResetEmail: string;
};
const { Input: RequestPasswordResetInput, Form: RequestPasswordResetForm } =
  createTypedForm<RequestPasswordResetFormType>();

const AUTH_ERROR =
  "Authentication failed. Please try again later or contact Backpack support if the issue persists.";
const EMAIL_ERROR = "Please enter a valid email address.";

const PAGE_ID_MAP = {
  credentials: 1,
  requestPasswordReset: 0,
} as const;

const HeightWrapper: FunctionComponent<{ height?: number }> = ({
  children,
  height,
}) => (
  <div className="flex flex-col justify-center" style={{ height }}>
    {children}
  </div>
);

export default function SignIn({
  isCurrentUserError,
}: Readonly<{ isCurrentUserError: boolean }>) {
  const [activeIndex, setActiveIndex] = useState<0 | 1>(
    PAGE_ID_MAP.credentials
  );
  const isPasswordResetRequest =
    PAGE_ID_MAP.requestPasswordReset === activeIndex;

  // PageTransition component does not transition smoothly from greater to smaller height
  // this ensures that transitions are only to greater height
  const [containerHeight, setContainerHeight] = useState<number>();
  const measureContainerHeight = useCallback((ref: HTMLDivElement | null) => {
    const height = ref?.getBoundingClientRect().height;
    setContainerHeight((prev) => {
      if (typeof prev === "number" && typeof height === "number") {
        return Math.max(height, prev);
      }
      if (typeof height === "number") return height;
      return prev;
    });
  }, []);

  return (
    <ErrorBoundary label="SignIn">
      <AuthWrapper>
        <PageTransition
          activeIndex={activeIndex}
          pageContainerClassName="h-full flex-col justify-center"
        >
          {activeIndex === PAGE_ID_MAP.credentials ? (
            <HeightWrapper height={containerHeight}>
              <Credentials
                isPasswordResetRequest={isPasswordResetRequest}
                measureContainerHeight={measureContainerHeight}
                setActiveIndex={setActiveIndex}
                isCurrentUserError={isCurrentUserError}
              />
            </HeightWrapper>
          ) : (
            <HeightWrapper height={containerHeight}>
              <RequestPasswordReset
                measureContainerHeight={measureContainerHeight}
                isPasswordResetRequest={isPasswordResetRequest}
                setActiveIndex={setActiveIndex}
              />
            </HeightWrapper>
          )}
        </PageTransition>
      </AuthWrapper>
    </ErrorBoundary>
  );
}

type FormProps = {
  isPasswordResetRequest: boolean;
  measureContainerHeight: (ref: HTMLDivElement | null) => void;
  setActiveIndex: (state: 0 | 1) => void;
};

function Credentials({
  isPasswordResetRequest,
  measureContainerHeight,
  setActiveIndex,
  isCurrentUserError,
}: Readonly<FormProps & { isCurrentUserError: boolean }>) {
  const [showPassword, setShowPassword] = useState(false);
  const { mutateAsync: handleSignIn, reset } = useMutation({
    mutationFn: async ({
      email,
      password,
    }: {
      email: string;
      password: string;
    }) => {
      try {
        await signInWithEmailAndPassword(auth, email, password);
      } catch (e) {
        if (e instanceof FirebaseError) {
          throwFirebaseError(e);
        }
        throw new Error(AUTH_ERROR);
      }
    },
  });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && !isPasswordResetRequest) {
        buttonRef.current?.click();
      }
    },
    [isPasswordResetRequest]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <Form
      labelColor="secondary"
      inputBackgroundColor="body"
      displayRequiredErrorsAfterSubmit
      onSubmit={async ({ email, password }) =>
        await handleSignIn({ email, password })
      }
      render={({ handleSubmit }) => (
        <form
          onSubmit={(e) => e.preventDefault()}
          noValidate
          className="w-full"
        >
          <div
            className="flex h-full w-full flex-col gap-4 pb-2.5 pt-5"
            ref={measureContainerHeight}
          >
            <Heading text="Welcome back!" />
            <div className="h-20">
              <Input
                name="email"
                dataTestId="email"
                autoComplete="email"
                placeholder="Enter Email"
                required
                validate={(val) => {
                  if (!val) return "This field is required";
                  if (!emailRegex.test(val)) return EMAIL_ERROR;
                }}
              />
            </div>
            <div className="relative h-20">
              <Input
                name="password"
                className="relative"
                dataTestId="password"
                autoComplete="current-password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter Password"
                required
              />
              <PasswordVisibilityToggle
                showPassword={showPassword}
                setShowPassword={setShowPassword}
              />
            </div>
            <div className="mt-2.5 flex items-center justify-between">
              <Button
                type="button"
                noFrameVariant="text"
                color="secondary"
                onClick={() => {
                  reset();
                  setActiveIndex(PAGE_ID_MAP.requestPasswordReset);
                }}
              >
                Forgot password?
              </Button>
              <AsyncButton
                width="120px"
                type="submit"
                color="positive"
                ref={buttonRef}
                onClick={async () => {
                  await handleSubmit();
                }}
              >
                Log in
              </AsyncButton>
            </div>
            <Text
              variant="sb_t-12-500"
              color="negative"
              className={classNames("grow-0 transition-opacity", {
                "opacity-0": !isCurrentUserError,
                "opacity-100": isCurrentUserError,
              })}
            >
              {AUTH_ERROR}
            </Text>
          </div>
        </form>
      )}
    />
  );
}

function RequestPasswordReset({
  measureContainerHeight,
  isPasswordResetRequest,
  setActiveIndex,
}: Readonly<FormProps>) {
  const {
    mutateAsync: handleSendPasswordResetEmail,
    isSuccess,
    reset,
  } = backpackSdk.auth.users.mutations.useRequestPasswordReset();

  const buttonRef = useRef<HTMLButtonElement>(null);
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter" && isPasswordResetRequest) {
        buttonRef.current?.click();
      }
    },
    [isPasswordResetRequest]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  return (
    <RequestPasswordResetForm
      inputBackgroundColor="body"
      labelColor="secondary"
      displayRequiredErrorsAfterSubmit
      onSubmit={async ({ passwordResetEmail }) =>
        await handleSendPasswordResetEmail(passwordResetEmail)
      }
      render={({ handleSubmit, form }) => (
        <form
          onSubmit={(e) => e.preventDefault()}
          noValidate
          className="w-full"
        >
          <div
            className="flex h-full w-full flex-col gap-4 pb-2.5 pt-5"
            ref={measureContainerHeight}
          >
            <div>
              <div className="pt-12">
                <Heading text="Password Reset" />
              </div>
            </div>
            <div className="flex w-full flex-col gap-4">
              <div className="h-20">
                <RequestPasswordResetInput
                  name="passwordResetEmail"
                  dataTestId="email-reset"
                  label="Enter your email to reset"
                  autoComplete="email"
                  placeholder="Enter Email"
                  required
                  validate={(val) => {
                    if (val && !emailRegex.test(val)) return EMAIL_ERROR;
                  }}
                />
              </div>
              <div className="mt-2.5 flex items-center justify-between pb-16">
                <Button
                  type="button"
                  noFrameVariant="combo"
                  color="secondary"
                  icon="caret-left"
                  onClick={() => {
                    setActiveIndex(PAGE_ID_MAP.credentials);
                    form.restart();
                    reset();
                  }}
                >
                  Back
                </Button>
                <AsyncButton
                  type="submit"
                  color="positive"
                  alertRenderLocation="bottomRight"
                  onClick={async () => {
                    await handleSubmit();
                  }}
                >
                  Send reset email
                </AsyncButton>
              </div>
              <div
                data-testid="password-reset-confirmation-message"
                className={classNames(
                  "flex gap-2 border-t border-straps-accent-3 pt-6 transition-opacity",
                  { "opacity-0": !isSuccess, "opacity-100": isSuccess }
                )}
              >
                <Icon name="info" color="tertiary" />
                <Text variant="sb_t-14-500">
                  If there is an account associated with this email address, an
                  email has been sent to reset your password.
                </Text>
              </div>
            </div>
          </div>
        </form>
      )}
    />
  );
}
